const DEFAULT_MAX_STRING_LENGTH = 2_000
const DEFAULT_MAX_STACK_LENGTH = 8_000
const DEFAULT_MAX_ARRAY_LENGTH = 50
const FALLBACK_BASE_URL = "https://client-error.local/"

type SanitizeOptions = {
  maxStringLength?: number
  maxStackLength?: number
  maxArrayLength?: number
}

const ABSOLUTE_OR_PROTOCOL_RELATIVE_URL =
  /((?:https?:)?\/\/[^\s"'<>()[\]{}]+)/gi
const PATH_WITH_QUERY_OR_HASH =
  /(^|[\s"'([])(\/[^\s"'<>()[\]{}?#]+[^\s"'<>()[\]{}]*[?#][^\s"'<>()[\]{}]*)/g
const RELATIVE_PATH_WITH_QUERY_OR_HASH =
  /(^|[\s"'([])((?:\.{1,2}\/)?[A-Za-z0-9._~-]+\/[A-Za-z0-9._~/-]*[?#][^\s"'<>()[\]{}]*)/g
const FILE_WITH_QUERY_OR_HASH =
  /(^|[\s"'([])([A-Za-z0-9._~-]+\.[A-Za-z0-9._~-]+[?#][^\s"'<>()[\]{}]+)/g
const BARE_QUERY_WITH_VALUE =
  /(^|[\s"'([])([A-Za-z0-9._~-]+\?[^=\s"'<>()[\]{}]+=[^\s"'<>()[\]{}]*)/g

export const isSanitizableUrlLike = (value: string | undefined | null) => {
  if (!value) return false

  const trimmed = value.trim()
  return (
    /^https?:\/\//i.test(trimmed) ||
    /^\/\//.test(trimmed) ||
    trimmed.startsWith("/") ||
    /^\.{1,2}\//.test(trimmed) ||
    /^[A-Za-z0-9._~-]+\//.test(trimmed) ||
    /^[A-Za-z0-9._~-]+\.[A-Za-z0-9._~-]+[?#]/.test(trimmed) ||
    /^[A-Za-z0-9._~-]+\?[^=\s"'<>()[\]{}]+=/.test(trimmed)
  )
}

const trimTrailingPunctuation = (value: string) => {
  const match = value.match(/[),.;:]+$/)
  if (!match) return { core: value, suffix: "" }

  return {
    core: value.slice(0, -match[0].length),
    suffix: match[0],
  }
}

export const sanitizeUrl = (value: string | undefined | null) => {
  if (!value) return undefined

  const trimmed = value.trim()
  if (!trimmed) return undefined

  const { core, suffix } = trimTrailingPunctuation(trimmed)

  if (/^https?:\/\//i.test(core) || /^\/\//.test(core)) {
    try {
      const url = new URL(
        core.startsWith("//") ? `https:${core}` : core,
        FALLBACK_BASE_URL,
      )
      return `${url.origin}${url.pathname}${suffix}`
    } catch {
      return undefined
    }
  }

  if (core.startsWith("/")) {
    try {
      const url = new URL(core, FALLBACK_BASE_URL)
      return `${url.pathname}${suffix}`
    } catch {
      return undefined
    }
  }

  try {
    const url = new URL(core, FALLBACK_BASE_URL)
    return `${url.pathname.replace(/^\//, "")}${suffix}`
  } catch {
    return undefined
  }
}

export const sanitizeUrlSubstrings = (value: string) =>
  value
    .replace(ABSOLUTE_OR_PROTOCOL_RELATIVE_URL, (match) => {
      const sanitized = sanitizeUrl(match)
      return sanitized ?? match
    })
    .replace(PATH_WITH_QUERY_OR_HASH, (match, prefix: string, path: string) => {
      const sanitized = sanitizeUrl(path)
      return `${prefix}${sanitized ?? path}`
    })
    .replace(
      RELATIVE_PATH_WITH_QUERY_OR_HASH,
      (match, prefix: string, path: string) => {
        const sanitized = sanitizeUrl(path)
        return `${prefix}${sanitized ?? path}`
      },
    )
    .replace(FILE_WITH_QUERY_OR_HASH, (match, prefix: string, path: string) => {
      const sanitized = sanitizeUrl(path)
      return `${prefix}${sanitized ?? path}`
    })
    .replace(BARE_QUERY_WITH_VALUE, (match, prefix: string, path: string) => {
      const sanitized = sanitizeUrl(path)
      return `${prefix}${sanitized ?? path}`
    })

export const truncateString = (
  value: string,
  maxLength = DEFAULT_MAX_STRING_LENGTH,
) =>
  value.length > maxLength
    ? `${value.slice(0, maxLength)}...[truncated]`
    : value

export const sanitizeReportString = (
  value: string,
  maxLength = DEFAULT_MAX_STRING_LENGTH,
) =>
  truncateString(
    sanitizeUrlSubstrings(value)
      .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, "Bearer [REDACTED]")
      .replace(
        /(accessToken|refreshToken|token|password|authorization)=([^&\s]+)/gi,
        "$1=[REDACTED]",
      ),
    maxLength,
  )

export const sanitizeReportValue = (
  value: unknown,
  options: SanitizeOptions = {},
): unknown => {
  const {
    maxStringLength = DEFAULT_MAX_STRING_LENGTH,
    maxStackLength = DEFAULT_MAX_STACK_LENGTH,
    maxArrayLength = DEFAULT_MAX_ARRAY_LENGTH,
  } = options

  const seen = new WeakSet<object>()

  const visit = (input: unknown, key = ""): unknown => {
    if (input == null) return input

    if (typeof input === "string") {
      const maxLength = /stack|componentStack/i.test(key)
        ? maxStackLength
        : maxStringLength
      return sanitizeReportString(input, maxLength)
    }

    if (Array.isArray(input)) {
      if (seen.has(input)) return "[Circular]"
      seen.add(input)

      return input.slice(0, maxArrayLength).map((item) => visit(item, key))
    }

    if (typeof input === "object") {
      if (seen.has(input)) return "[Circular]"
      seen.add(input)

      const out: Record<string, unknown> = {}

      Object.entries(input).forEach(([entryKey, entryValue]) => {
        out[entryKey] = /token|password|authorization|cookie|secret|session/i.test(
          entryKey,
        )
          ? "[REDACTED]"
          : visit(entryValue, entryKey)
      })

      return out
    }

    return input
  }

  return visit(value)
}
